import type {
  TimeclockMark,
  TimeclockSheetDay,
  TimeclockSheetResponse,
} from '@aeris/contracts'
import type { PrintDocument } from '@aeris/printing'

import { textValue, type ExportColumn } from './export/table-export.ts'
import { PUNCH_LABELS } from './ponto-device.ts'

/**
 * O espelho do mês (F340/F341) para o papel e para a planilha: as mesmas
 * contas da tela, sem nada novo.
 */

const TIME_ZONE = 'America/Sao_Paulo'

/** `485` → `8h05`; zero vira `—`. */
export const minutesText = (minutes: number): string =>
  minutes === 0
    ? '—'
    : `${String(Math.floor(minutes / 60))}h${String(minutes % 60).padStart(2, '0')}`

export const clockText = (iso: string): string =>
  new Intl.DateTimeFormat('pt-BR', {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))

const WEEKDAY = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'UTC',
  weekday: 'short',
})

/** `2026-10-27` → `27/10 ter`. */
export const dayText = (day: string): string => {
  const [, m, d] = day.split('-')
  const weekday = WEEKDAY.format(new Date(`${day}T12:00:00Z`)).replace('.', '')
  return `${d ?? ''}/${m ?? ''} ${weekday}`
}

export const shiftText = (shift: TimeclockSheetDay['shift']): string =>
  shift === null
    ? '—'
    : shift.kind === 'folga'
      ? shift.name
      : `${shift.name} (${shift.startsAt ?? ''}–${shift.endsAt ?? ''})`

const markText = (mark: TimeclockMark): string =>
  `${PUNCH_LABELS[mark.kind]} ${clockText(mark.at)}${mark.source === 'ajuste' ? '*' : ''}`

/** As marcas que contam, na ordem. A anulada fica de fora do papel. */
export const marksText = (day: TimeclockSheetDay): string =>
  day.marks
    .filter((mark) => mark.voided === null)
    .map(markText)
    .join(', ')

export const situationText = (day: TimeclockSheetDay): string =>
  [
    day.absent ? 'Falta' : null,
    day.incomplete ? 'Incompleto' : null,
    day.shortMinutes > 0 ? `Faltou ${minutesText(day.shortMinutes)}` : null,
    day.note === 'trabalhou_na_folga' ? 'Trabalhou na folga' : null,
    day.note === 'folga' ? 'Folga' : null,
    day.adjusted ? 'Ajustado' : null,
  ]
    .filter((part) => part !== null)
    .join(', ')

const monthTitle = (month: string): string => {
  const [y, m] = month.split('-')
  return `${m ?? ''}/${y ?? ''}`
}

/** A folha A4 do mês, com a linha para a assinatura do funcionário. */
export const sheetPrintFor = (
  sheet: TimeclockSheetResponse,
  company: { readonly name: string }
): PrintDocument => ({
  kind: 'report',
  title: `Espelho do ponto — ${sheet.employee.name}`,
  blocks: [
    { kind: 'text', text: company.name, align: 'center', bold: true },
    {
      kind: 'text',
      text: `Espelho do ponto — ${monthTitle(sheet.month)}`,
      align: 'center',
      bold: true,
    },
    { kind: 'text', text: 'Controle interno', align: 'center' },
    { kind: 'rule' },
    { kind: 'pair', label: 'Funcionário', value: sheet.employee.name },
    { kind: 'pair', label: 'Loja', value: sheet.employee.branchName },
    { kind: 'rule' },
    {
      kind: 'table',
      columns: [
        { label: 'Dia' },
        { label: 'Escala' },
        { label: 'Marcas' },
        { label: 'Trabalhado', align: 'right' },
        { label: 'Atraso', align: 'right' },
        { label: 'Extra', align: 'right' },
        { label: 'Situação' },
      ],
      rows: sheet.days.map((day) => [
        dayText(day.day),
        shiftText(day.shift),
        marksText(day),
        minutesText(day.workedMinutes),
        minutesText(day.lateMinutes + day.earlyMinutes),
        minutesText(day.extraMinutes),
        situationText(day),
      ]),
      footer: [
        'Total',
        minutesText(sheet.totals.scheduledMinutes),
        `${String(sheet.totals.absences)} falta(s)`,
        minutesText(sheet.totals.workedMinutes),
        minutesText(sheet.totals.lateMinutes + sheet.totals.earlyMinutes),
        minutesText(sheet.totals.extraMinutes),
        `${String(sheet.totals.incompleteDays)} incompleto(s)`,
      ],
    },
    { kind: 'text', text: '* incluída pelo gerente, com o motivo no sistema.' },
    { kind: 'feed', lines: 3 },
    {
      kind: 'text',
      text: '______________________________________',
      align: 'center',
    },
    { kind: 'text', text: sheet.employee.name, align: 'center' },
  ],
})

/** As colunas da planilha do espelho. */
export const SHEET_COLUMNS: readonly ExportColumn<TimeclockSheetDay>[] = [
  { id: 'dia', label: 'Dia', value: (day) => ({ date: day.day }) },
  {
    id: 'escala',
    label: 'Escala',
    value: (day) => textValue(shiftText(day.shift)),
  },
  { id: 'marcas', label: 'Marcas', value: (day) => textValue(marksText(day)) },
  {
    id: 'trabalhado_min',
    label: 'Trabalhado (min)',
    value: (day) => ({ number: String(day.workedMinutes) }),
  },
  {
    id: 'previsto_min',
    label: 'Previsto (min)',
    value: (day) => ({ number: String(day.scheduledMinutes) }),
  },
  {
    id: 'atraso_min',
    label: 'Atraso (min)',
    value: (day) => ({ number: String(day.lateMinutes) }),
  },
  {
    id: 'saida_antes_min',
    label: 'Saída antes (min)',
    value: (day) => ({ number: String(day.earlyMinutes) }),
  },
  {
    id: 'extra_min',
    label: 'Extra (min)',
    value: (day) => ({ number: String(day.extraMinutes) }),
  },
  {
    id: 'faltou_min',
    label: 'Faltou (min)',
    value: (day) => ({ number: String(day.shortMinutes) }),
  },
  {
    id: 'situacao',
    label: 'Situação',
    value: (day) => textValue(situationText(day)),
  },
]
